import { Component, computed, ElementRef, Inject, inject, Input, OnChanges, OnInit, Optional, signal, SimpleChanges, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgbAccordionModule, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { NgxSliderModule } from '@angular-slider/ngx-slider';
import { GridStyle, LayerDetails, LayerType, OptionalLayer, SeedableLayerDetails } from '../types';
import { LayerService, MapDefinition, WmsData, WmsLayerData, WmsRegion } from '../services/layer.service';
import { OfflineLayerService } from '../services/offline-layer.service';
import { MapStatusService } from '../services/map-status.service';
import { Command } from '../../core/types/command.enum';
import { NAVBAR_PROVIDER, NavbarProvider, PROJECT_PROVIDER, ProjectProvider, DATA_PROVIDER, DataProvider, CONFIRM_SERVICE, IConfirmService, GEODATA_API_SERVICE, IGeodataApiService } from '../../core';
import { StorageService } from '../../services/storage/storage.service';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { LatLngBounds } from 'leaflet';
import { cloneDeep } from 'lodash';
import { TreeChild, TreeParent, CheckboxTreeComponent } from '../project/checkbox-tree.component';
import { ConnectionService } from '../../services/connection.service';
import { OfflineModeService } from '../../services/offline-mode.service';
import { ZeichnungslayerDefinition } from '../types/zeichnungslayer.types';
import { DrawStyleModalComponent } from '../controls/draw-style/draw-style-modal.component';
import { ControlsIconsModule } from '../../controls/icons.module';
import { GridControlsComponent } from '../controls/grid-controls/grid-controls.component';
import { DgmCalibrationService } from '../services/dgm-calibration.service';
import { firstValueFrom, last } from 'rxjs';

@Component({
    selector: 'bp-layer-store',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        NgbAccordionModule,
        NgbTooltipModule,
        NgxSliderModule,
        CheckboxTreeComponent,
        ControlsIconsModule,
        GridControlsComponent
    ],
    templateUrl: './layer-store.component.html',
    styleUrls: ['./layer-store.component.css', '../accordion.css'],
})
export class LayerStoreComponent implements OnInit, OnChanges {
	activeModal = inject(NgbActiveModal);
	private modalService = inject(NgbModal);
	storage = inject(StorageService);

	@Input() project!: string;

	@ViewChild("attachmentFileInput") attachmentFileInput!: ElementRef<HTMLInputElement>;

	trackByIndex = (index: number, ld: LayerDetails) => index;
	mapDef!: MapDefinition;
	
	wmsLayers = signal<TreeParent[]>([]);
	attachments = signal<TreeParent[]>([]);
	missingGeodataFiles = signal<string[]>([]);

	// Edit-Modus für WMS: Zeigt alle verfügbaren WMS-Dienste
	editModeWms = signal<boolean>(false);

	// Zeichnungslayer
	zeichnungslayer = signal<ZeichnungslayerDefinition[]>([]);

	// Punktgitter
	gridEnabled = signal<boolean>(false);
	gridResolution = signal<number>(5);  // Hektar pro Punkt
	gridRadius = signal<number>(3);  // Punktradius in Pixel
	gridStyle = signal<GridStyle>({ color: '#3388ff', fillColor: '#3388ff', fillOpacity: 0.5, radius: 3 });

	// Flag um doppelte Attachment-Updates zu verhindern
	private isAddingAttachment = false;

	// Nur verwendete WMS-Dienste (mit aktivierten Layern)
	usedWmsLayers = computed(() =>
		this.wmsLayers().filter(node => node.checked || node.indeterminate || (node.children && node.children.length > 0))
	);

	// Angezeigte WMS-Dienste: Im Edit-Modus alle, sonst nur verwendete
	// Im Offline-Modus immer nur verwendete (Edit-Modus nicht verfügbar)
	displayedWmsLayers = computed(() => {
		const editMode = this.editModeWms() && !this.oms.offlineMode();
		if (editMode) {
			return this.wmsLayers();
		}
		// Normal-Modus: nur aktivierte Sublayer anzeigen (neue Objekte, da nur Ansicht)
		return this.usedWmsLayers().map(node => ({
			...node,
			checked: node.children?.some(c => c.checked) ?? false,
			children: node.children?.filter(c => c.checked)
		}));
	});

	C = Command;

	constructor(
		public cs: ConnectionService,
		@Inject(DATA_PROVIDER) public ds: DataProvider,
		public ls: LayerService,
		private ms: MapStatusService,
		@Inject(NAVBAR_PROVIDER) public ns: NavbarProvider,
		public ols: OfflineLayerService,
		@Inject(PROJECT_PROVIDER) public ps: ProjectProvider,
		public oms: OfflineModeService,
		@Inject(CONFIRM_SERVICE) private confirmService: IConfirmService,
		@Optional() @Inject(GEODATA_API_SERVICE) private geodataApi: IGeodataApiService | null,
		private dgmCalibration: DgmCalibrationService
	) {
		// Nur aktualisieren wenn nicht gerade ein Attachment hinzugefügt wird
		this.ls.attachmentsChanged.subscribe(() => {
			if (!this.isAddingAttachment) {
				this.onUpdateAttachmentList();
			}
		});
		if (this.ls.mapDef()) {
			this.mapDef = cloneDeep(this.ls.mapDef());
			// Duplikate entfernen (nach Namen, letztes Vorkommen behalten)
			const seen = new Set<string>();
			this.mapDef.layers = this.mapDef.layers.filter((ld, idx, arr) => {
				// Vom Ende her prüfen - letztes Vorkommen behalten
				for (let i = arr.length - 1; i >= 0; i--) {
					if (arr[i].name === ld.name) {
						return idx === i;
					}
				}
				return true;
			});
			this.mapDef.layers = this.mapDef.layers.reverse();
		}
		else {
			this.mapDef = {layers: []};
		}
	}

	ngOnInit(): void {
		this.onUpdateAttachmentList();
		this.loadZeichnungslayer();
		this.initWmsLayers();
		this.initGridLayer();
	}

	private initWmsLayers(): void {
		const projectBounds = this._getProjectBounds();
		const projectRegions = this._detectProjectRegions(projectBounds);

		// WMS-Dienste synchron initialisieren - Filter nach Region
		const nodes: TreeParent[] = [];

		this.ls.wmsServerMap.forEach((wmsData: WmsData, name: string) => {
			// Bereits verwendete WMS-Dienste immer anzeigen
			const isUsed = this.mapDef.layers.some((ld: LayerDetails) => ld.type === 'wms' && ld.name === name && ld.layers?.some(l => l.enabled));

			// Filter nach Region: BB nur für BB, MV nur für MV, DE für alle in DE, global für alle
			// Bei Überlappung werden alle passenden Regionen angezeigt
			if (!isUsed && projectRegions.length > 0 && wmsData.region) {
				if (!this._isRegionCompatible(wmsData.region, projectRegions)) {
					return; // WMS-Dienst nicht in der Projektregion verfügbar
				}
			}

			const serverNode: TreeParent = {
				id: name,
				label: name,
				type: 'wms',
				// children NICHT setzen - wird bei Bedarf über loadChildren geladen
			};

			const ld = this.mapDef.layers.find((ld: LayerDetails) => ld.type === 'wms' && ld.name === name && ld.layers);
			if (ld && ld.layers?.some((l: OptionalLayer) => l.enabled)) {
				// Es gibt bereits aktivierte Layer
				serverNode.checked = true;
				serverNode.children = [];

				ld.layers.forEach((l: OptionalLayer) => {
					const childNode: TreeChild = {
						id: `${name}##${l.name}`,
						label: l.title || l.name,
						checked: l.enabled || false
					};
					serverNode.children!.push(childNode);
				});

				// Parent ist checked wenn mindestens ein Child checked
				const anyChecked = serverNode.children.some(c => c.checked);
				serverNode.checked = anyChecked;
			}
			nodes.push(serverNode);
		});

		this.wmsLayers.set(nodes);
	}

	// Regionen-Bounding-Boxes (präziser, minimale Überlappung an der Landesgrenze)
	private static readonly REGION_BOUNDS: Record<string, LatLngBounds> = {
		'BB': new LatLngBounds([51.35, 11.26], [53.40, 14.77]),  // Brandenburg (Nordgrenze ~53.4°)
		'MV': new LatLngBounds([53.35, 10.59], [54.68, 14.41]),  // Mecklenburg-Vorpommern (Südgrenze ~53.35°)
		'DE': new LatLngBounds([47.27, 5.87], [55.06, 15.04])    // Deutschland
	};

	/**
	 * Erkennt alle Regionen, die den Projektbereich überlappen.
	 * Nutzt intersects statt contains(center), damit Projekte die BB und MV umfassen
	 * beide Regionen erkennen.
	 */
	private _detectProjectRegions(projectBounds: LatLngBounds | undefined): WmsRegion[] {
		if (!projectBounds) return [];

		const regions: WmsRegion[] = [];

		// Alle Bundesländer prüfen, die den Projektbereich überlappen
		if (projectBounds.intersects(LayerStoreComponent.REGION_BOUNDS['BB'])) {
			regions.push('BB');
		}
		if (projectBounds.intersects(LayerStoreComponent.REGION_BOUNDS['MV'])) {
			regions.push('MV');
		}

		// Wenn mindestens ein Bundesland erkannt wurde, auch DE hinzufügen
		if (regions.length > 0) {
			regions.push('DE');
		} else if (projectBounds.intersects(LayerStoreComponent.REGION_BOUNDS['DE'])) {
			// Nur DE wenn kein spezifisches Bundesland
			regions.push('DE');
		}

		// Fallback: global wenn nichts erkannt
		if (regions.length === 0) {
			regions.push('global');
		}

		return regions;
	}

	private _isRegionCompatible(wmsRegion: WmsRegion, projectRegions: WmsRegion[]): boolean {
		// global ist immer kompatibel
		if (wmsRegion === 'global') return true;
		// WMS-Region muss in einer der Projektregionen enthalten sein
		return projectRegions.includes(wmsRegion);
	}

	private _getProjectBounds(): LatLngBounds | undefined {
		const projectRegions = this.ls.regions()?.regions ?? [];
		if (projectRegions.length === 0) return undefined;

		return projectRegions.reduce((acc, r) => {
			// Bounds können serialisiert sein - rekonstruieren
			const b = r.bounds as any;
			const bounds = b._southWest
				? new LatLngBounds(b._southWest, b._northEast)
				: r.bounds;
			return acc ? acc.extend(bounds) : new LatLngBounds(bounds.getSouthWest(), bounds.getNorthEast());
		}, undefined as LatLngBounds | undefined);
	}

	private _updateMapDefFromAttachments() {
		this.attachments().forEach((node: TreeParent) => {
			let ld = this.mapDef.layers.find((ld: LayerDetails) => ld.name === node.id);
			const isRelevant = node.checked || node.indeterminate || (ld && ld.layers?.some((l: OptionalLayer) => l.enabled));
			if (!ld && isRelevant) {
				// Ausgewählter Knoten existiert noch nicht in mapDef
				ld = {
					type: node.type,
					name: node.id as string,
					url: node.id as string,
					isBase: false,
					minZoom: 0,
					maxZoom: 18,
					transparent: true
				};
				// layers nur setzen wenn es Sublayers gibt
				if (node.children && node.children.length > 0) {
					ld.layers = [];
				}
				this.mapDef.layers.unshift(ld!);
			}
			if (ld && !isRelevant) {
				// Nicht ausgewählter Knoten: Entfernen aus mapDef
				this.mapDef.layers = this.mapDef.layers.filter((layer: LayerDetails) => layer !== ld);
				return;
			}
			if (ld && isRelevant) {
				if (node.children && node.children.length > 0) {
					ld.layers = node.children.map((childNode: TreeChild) => ({
						name: (childNode.id as string).split('##')[1],
						title: childNode.label,
						enabled: childNode.checked || false,
						transparent: true,
						type: childNode.type
					}));
					if (!ld.layers.some((l: OptionalLayer) => l.enabled))
						this.mapDef.layers = this.mapDef.layers.filter((layer: LayerDetails) => layer !== ld);
				}
			}
		});
	}

	private _updateMapDefFromLayers() {
		this.wmsLayers().forEach((serverNode: TreeParent) => {
			let ld = this.mapDef.layers.find((ld: LayerDetails) => ld.type === 'wms' && ld.name === serverNode.id && ld.layers);
			const isRelevant = serverNode.checked || serverNode.indeterminate || (ld && ld.layers?.some((l: OptionalLayer) => l.enabled));
			if (!ld && isRelevant) {
				// Ausgewählter Knoten existiert noch nicht in mapDef
				const wmsData = this.ls.wmsServerMap.get(serverNode.id as string);
				if (!wmsData) return;
				const bbox = wmsData.layers && wmsData.layers.length > 0 ? wmsData.layers[0]?.bbox : this.ms.mapLimits.maxSubBounds;
				ld = {
					type: 'wms',
					url: wmsData.url,
					name: wmsData.name,
					isBase: false,
					minZoom: 0,
					maxZoom: 18,
					maxNativeZoom: wmsData.maxNativeZoom,  // Für Hochskalierung bei höheren Zoomstufen
					transparent: true,
					bbox: bbox,
					layers: [],
					wmsServerName: wmsData.name  // Für Auth-Lookup
				};
				this.mapDef.layers.unshift(ld);
			}
			if (ld && !isRelevant) {
				// Nicht ausgewählter Knoten: Entfernen aus mapDef
				this.mapDef.layers = this.mapDef.layers.filter((layer: LayerDetails) => layer !== ld);
				return;
			}
			if (ld && isRelevant) {
				const resultLayers: OptionalLayer[] = [];
				serverNode.children?.forEach((childNode: TreeChild) => {
					const rawName = (childNode.id as string).split('##')[1];
					// DGM kombinierter Eintrag:
					// - Ohne Kalibrierung: Ein WMS-Layer (hoehendarstellung_schummerung)
					// - Mit Kalibrierung: Zwei Layer (hoehendarstellung + schummerung_NW) für Pixel-Transformation + Multiply-Blend
					if (rawName === '__dgm_combined__') {
						if (this.dgmCalibration.settings()) {
							resultLayers.push({ name: 'hoehendarstellung', title: 'DGM Höhenfarben + Schummerung', enabled: childNode.checked || false, transparent: true });
							resultLayers.push({ name: 'schummerung_NW', title: '__dgm_schummerung__', enabled: childNode.checked || false, transparent: true });
						} else {
							resultLayers.push({ name: 'hoehendarstellung_schummerung', title: 'DGM Höhenfarben + Schummerung', enabled: childNode.checked || false, transparent: true });
						}
					} else {
						resultLayers.push({ name: rawName, title: childNode.label, enabled: childNode.checked || false, transparent: true });
					}
				});
				ld.layers = resultLayers;
				if (!ld.layers?.some((l: OptionalLayer) => l.enabled))
					this.mapDef.layers = this.mapDef.layers.filter((layer: LayerDetails) => layer !== ld);
			}
		});
	}
	
	ngOnChanges(changes: SimpleChanges): void { }

	onAttachmentSelectionChange($event: any) {
		this._updateMapDefFromAttachments();
	}

	onLayerSelectionChange($event: any) {
		this._updateMapDefFromLayers();
		// Signal-Update triggern damit computed values neu berechnet werden
		this.wmsLayers.set([...this.wmsLayers()]);
	}
	
	async onLoadChildrenForAttachmentNode(node: TreeParent): Promise<TreeChild[]> {
		const meta = await this.ps.getProjectValue(node.id as string, 'attachments_meta');

		const children: TreeChild[] = [];
		(meta as any)?.features.forEach((layer: string) => {
			const layerNode: TreeChild = {
				id: `${node.id}##${layer}`,
				label: layer,
				type: 'feature'
			};
			children!.push(layerNode);
		});
		(meta as any)?.tiles.forEach((layer: string) => {
			const layerNode: TreeChild = {
				id: `${node.id}##${layer}`,
				label: layer,
				type: 'tile'
			};
			children!.push(layerNode);
		});
		return children;
	}
	
	async onLoadChildrenForWmsServiceNode(node: TreeParent): Promise<TreeChild[]> {
		// Netzwerk-Check: Im Online-Modus ohne Netzwerk keine WMS-Layer laden
		if (!this.oms.userOfflineMode() && !this.oms.networkOnline()) {
			console.warn('WMS-Layer können nicht geladen werden: Keine Netzwerkverbindung');
			return [];
		}
		const layers = await this.ls.resolveWmsLayers(node.id as string);
		const children: TreeChild[] = [];

		// Prüfen welche Sublayer bereits aktiviert sind
		const ld = this.mapDef.layers.find((ld: LayerDetails) => ld.type === 'wms' && ld.name === node.id);
		const enabledLayers = new Set(ld?.layers?.filter(l => l.enabled).map(l => l.name) ?? []);

		// DGM-WMS: Nur einen kombinierten Eintrag anbieten
		const wmsUrl = (ld?.url || this.ls.wmsServerMap.get(node.id as string)?.url || '').toLowerCase();
		const isDgm = wmsUrl.includes('dgm_wms') || wmsUrl.includes('dgm/service');
		if (isDgm && layers.some((l: any) => l.name?.includes('hoehendarstellung'))) {
			children.push({
				id: `${node.id}##__dgm_combined__`,
				label: 'Höhenfarben + Schummerung',
				checked: enabledLayers.has('hoehendarstellung') || enabledLayers.has('hoehendarstellung_schummerung')
			});
			return children;
		}

		layers.forEach((wmsLayer: WmsLayerData) => {
			const layerNode: TreeChild = {
				id: `${node.id}##${wmsLayer.name}`,
				label: wmsLayer.title || wmsLayer.name,
				checked: enabledLayers.has(wmsLayer.name)
			};
			children.push(layerNode);
		});
		return children;
	}
	
	async onUpdateAttachmentList() {
		const geoFileTypes = new Set(['geotiff', 'gpkg', 'geojson']);

		// Nur Geodaten-Layer aus der mapDefinition anzeigen
		const geodataLayers = this.mapDef.layers.filter(ld => geoFileTypes.has(ld.type));
		const existingKeys = await this.ps.listKeys('attachments') ?? [];

		this.attachments.set([]);
		for (const ld of geodataLayers) {
			const hasSublayers = ld.layers && ld.layers.length > 0;
			// gpkg kann Sublayers haben - wenn noch keine geladen, nicht als checked markieren
			const canHaveSublayers = ld.type === 'gpkg';
			const attachmentNode: TreeParent = {
				id: ld.name,
				label: ld.name,
				type: ld.type as LayerType,
				// Für Layer mit Sublayers: checked wenn mindestens ein Sublayer enabled
				// Für Layer die Sublayers haben könnten aber noch keine haben: nicht checked
				// Für Layer ohne Sublayers (z.B. geotiff): checked basiert auf display
				checked: hasSublayers
					? !!ld.layers?.some((l: OptionalLayer) => l.enabled)
					: canHaveSublayers ? false : ld.display !== false
			};

			if (hasSublayers) {
				attachmentNode.children = [];
				ld.layers!.forEach((l: OptionalLayer) => {
					const childNode: TreeChild = {
						id: `${ld.name}##${l.name}`,
						label: l.title || l.name,
						type: l.type,
						checked: l.enabled || false
					};
					attachmentNode.children!.push(childNode);
				});
			}

			this.attachments.update(old => [...old, attachmentNode]);
		}

		// Prüfen ob Geodatendateien aus der mapDefinition fehlen
		await this.checkMissingGeodataFiles(existingKeys);
	}

	private async checkMissingGeodataFiles(existingKeys: string[]): Promise<void> {
		const geoFileTypes = new Set(['geotiff', 'gpkg', 'geojson']);
		const missingFiles: string[] = [];

		// Prüfe welche Geodaten-Layer aus der mapDefinition nicht im Storage sind
		for (const ld of this.mapDef.layers) {
			if (geoFileTypes.has(ld.type) && !existingKeys.includes(ld.name)) {
				missingFiles.push(ld.name);
			}
		}

		this.missingGeodataFiles.set(missingFiles);
	}

	async onRestoreMissingFile(filename: string): Promise<void> {
		// Netzwerk-Check: Im Online-Modus ohne Netzwerk keine Dateien wiederherstellen
		if (!this.oms.userOfflineMode() && !this.oms.networkOnline()) {
			await this.confirmService.warning('Keine Netzwerkverbindung - Datei kann nicht wiederhergestellt werden.');
			return;
		}
		const success = await this.ls.addMissingAttachment(filename);
		if (success) {
			await this.onUpdateAttachmentList();
		}
	}

	// Öffnet das Style-Modal für einen GPKG-Sublayer
	onOpenGpkgStyleModal(event: { parent: TreeParent, child: TreeChild }): void {
		const gpkgName = event.parent.id as string;
		const sublayerName = (event.child.id as string).split('##')[1];
		const fullLayerName = `${gpkgName}_${sublayerName}`;

		// Gespeicherten Style laden oder Standard-Style verwenden
		const storageKey = `gpkgStyle_${fullLayerName}`;
		const savedStyle = this.storage.getSync(storageKey) || {
			color: '#3388ff',
			weight: 2,
			fillColor: 'transparent',
			fillOpacity: 0
		};

		// Modal öffnen
		const modalRef = this.modalService.open(DrawStyleModalComponent, {
			centered: true
		});

		modalRef.componentInstance.gpkgMode = true;
		modalRef.componentInstance.gpkgLayerName = sublayerName;
		modalRef.componentInstance.layerStyle = {
			strokeColor: savedStyle.color || '#3388ff',
			strokeWidth: savedStyle.weight || 2,
			fillColor: savedStyle.fillColor || 'transparent',
			fillOpacity: savedStyle.fillOpacity || 0
		};

		modalRef.result.then((newStyle) => {
			if (newStyle) {
				// Style speichern
				const styleToSave = {
					color: newStyle.strokeColor,
					weight: newStyle.strokeWidth,
					fillColor: newStyle.fillColor,
					fillOpacity: newStyle.fillOpacity
				};
				this.storage.setSync(storageKey, styleToSave);

				// Layer-Service über Änderung informieren
				this.ls.layersChanged.emit();
			}
		}).catch(() => {});
	}

	// Öffnet das Style-Modal für einen GeoJSON-Layer (ohne Sublayer)
	onOpenGeoJsonStyleModal(node: TreeParent): void {
		const layerName = node.id as string;

		// Gespeicherten Style laden oder Standard-Style verwenden
		const storageKey = `geojsonStyle_${layerName}`;
		const savedStyle = this.storage.getSync(storageKey) || {
			color: 'darkslategray',
			weight: 2,
			fillColor: 'transparent',
			fillOpacity: 0
		};

		// Modal öffnen
		const modalRef = this.modalService.open(DrawStyleModalComponent, {
			centered: true
		});

		modalRef.componentInstance.gpkgMode = true;  // Gleiche Darstellung wie GPKG
		modalRef.componentInstance.gpkgLayerName = layerName;
		modalRef.componentInstance.layerStyle = {
			strokeColor: savedStyle.color || 'darkslategray',
			strokeWidth: savedStyle.weight || 2,
			fillColor: savedStyle.fillColor || 'transparent',
			fillOpacity: savedStyle.fillOpacity || 0
		};

		modalRef.result.then((newStyle) => {
			if (newStyle) {
				// Style speichern
				const styleToSave = {
					color: newStyle.strokeColor,
					weight: newStyle.strokeWidth,
					fillColor: newStyle.fillColor,
					fillOpacity: newStyle.fillOpacity
				};
				this.storage.setSync(storageKey, styleToSave);

				// Layer-Service über Änderung informieren
				this.ls.layersChanged.emit();
			}
		}).catch(() => {});
	}

	async onDeleteAttachment(node: TreeParent): Promise<void> {
		const filename = node.id as string;
		const confirmed = await this.confirmService.confirmDelete(`Geodatendatei "${filename}" wirklich löschen?`);
		if (!confirmed) return;

		// Aus Storage entfernen
		await this.ps.removeProjectValue(filename, 'attachments');
		await this.ps.removeProjectValue(filename, 'attachments_meta');
		await this.ps.removeGeodataFile(filename);

		// Auch auf Server löschen (fire-and-forget)
		if (this.geodataApi && this.oms.networkOnline()) {
			this.geodataApi.delete(this.ps.project(), filename).subscribe({
				error: err => console.warn('[LayerStore] Server-Löschung fehlgeschlagen:', err)
			});
		}

		// Aus mapDef entfernen
		this.mapDef.layers = this.mapDef.layers.filter(ld => ld.name !== filename);

		// Liste aktualisieren
		await this.onUpdateAttachmentList();
	}

	async onFileAdded(event: Event) {
		if (!event || !event.target || !(event.target instanceof HTMLInputElement)) return;
		const fi = event.target as HTMLInputElement;
		if (!fi.files || fi.files.length === 0) return;

		const files = Array.from(fi.files);

		// Hauptdatei finden (erste .shp, .zip, .7z, .gpkg, .tif, .geojson, etc.)
		const mainFile = files.find(f => {
			const lower = f.name.toLowerCase();
			return lower.endsWith('.shp') || lower.endsWith('.zip') || lower.endsWith('.7z') || lower.endsWith('.gpkg') ||
				lower.endsWith('.tif') || lower.endsWith('.tiff') ||
				lower.endsWith('.geojson') || lower.endsWith('.json');
		}) || files[0];

		const fileType = this.getAttachmentType(mainFile.name);

		if (!fileType) {
			await this.confirmService.warning(`Dateityp nicht unterstützt: ${mainFile.name}\n\nUnterstützte Formate: .gpkg, .tif, .tiff, .geojson, .json, .shp, .zip, .7z`);
			fi.value = '';
			return;
		}

		// Companion-Dateien für Shapefiles sammeln
		const companionFiles = mainFile.name.toLowerCase().endsWith('.shp')
			? files.filter(f => {
				const lower = f.name.toLowerCase();
				return lower.endsWith('.dbf') || lower.endsWith('.prj') || lower.endsWith('.shx');
			})
			: undefined;

		// Flag setzen um doppelte Updates zu verhindern
		this.isAddingAttachment = true;

		// Für Shapefiles wird der Name zu .geojson geändert
		const layerName = mainFile.name.toLowerCase().endsWith('.shp') || mainFile.name.toLowerCase().endsWith('.zip') || mainFile.name.toLowerCase().endsWith('.7z')
			? mainFile.name.replace(/\.(shp|zip|7z)$/i, '.geojson')
			: mainFile.name;

		// Eindeutigen Namen generieren falls Layer bereits existiert
		const existingNames = new Set(this.mapDef.layers.map(ld => ld.name));
		let uniqueName = layerName;
		if (existingNames.has(layerName)) {
			const dotIndex = layerName.lastIndexOf('.');
			const baseName = dotIndex > 0 ? layerName.substring(0, dotIndex) : layerName;
			const ext = dotIndex > 0 ? layerName.substring(dotIndex) : '';
			let counter = 2;
			uniqueName = `${baseName}_${counter}${ext}`;
			while (existingNames.has(uniqueName)) {
				counter++;
				uniqueName = `${baseName}_${counter}${ext}`;
			}
		}

		try {
			// Neue Geodaten-Datei zur mapDef hinzufügen
			const layer: LayerDetails = {
				type: fileType,
				name: uniqueName,
				url: uniqueName,
				isBase: false,
				minZoom: 0,
				maxZoom: 18,
				transparent: true,
				display: true,
				resolution: fileType === 'geotiff' ? 256 : undefined
				// layers wird nur für GPKG gesetzt (in der GPKG-Verarbeitung unten)
			};
			this.mapDef.layers.unshift(layer);

			// Warte auf vollständiges Speichern der Datei
			await this.ls.addAttachment(mainFile, this.mapDef, companionFiles, uniqueName);

			// Für GPKG: Sublayer aus Metadaten laden und aktivieren
			if (fileType === 'gpkg') {
				const meta = await this.ps.getProjectValue(uniqueName, 'attachments_meta');
				if (meta) {
					layer.layers = [];
					(meta as any).features?.forEach((name: string) => {
						layer.layers!.push({ name, title: name, enabled: true, transparent: true, type: 'feature' });
					});
					(meta as any).tiles?.forEach((name: string) => {
						layer.layers!.push({ name, title: name, enabled: true, transparent: true, type: 'tile' });
					});
				}
			}

			// MapDef mit neuen Layern speichern und Signal aktualisieren
			// WICHTIG: layers sind in der UI umgekehrt, also wieder umkehren für die Speicherung
			const mapDefToSave = { layers: [...this.mapDef.layers].reverse() };
			this.ls.mapDef.set(mapDefToSave);
			await this.ps.setProjectValue("mapDefinition", mapDefToSave);

			// Server-Upload anbieten (nur wenn API verfügbar und online)
			if (this.geodataApi && this.oms.networkOnline()) {
				const shouldUpload = await this.confirmService.confirm({
					title: 'Geodaten hochladen',
					message: `"${uniqueName}" wurde lokal gespeichert. Soll die Datei auch auf den Server hochgeladen werden, damit andere Projektmitglieder sie nutzen können?`,
					confirmText: 'Hochladen',
					cancelText: 'Nur lokal'
				});
				if (shouldUpload) {
					try {
						const buf = await this.ps.getProjectValue(uniqueName, 'attachments');
						if (buf) {
							await firstValueFrom(
								this.geodataApi.upload(this.ps.project(), uniqueName, buf as ArrayBuffer | string).pipe(last())
							);
						}
					} catch (err) {
						console.warn('[LayerStore] Geodaten-Upload fehlgeschlagen:', err);
						await this.confirmService.warning(
							'Die Datei wurde lokal gespeichert, konnte aber nicht auf den Server hochgeladen werden.'
						);
					}
				}
			}

			// Liste aktualisieren
			await this.onUpdateAttachmentList();

			// Karte aktualisieren damit der neue Layer angezeigt wird
			this.ls.layersChanged.emit();
		} catch (e) {
			// Bei Fehler: Layer wieder entfernen und Fehlermeldung zeigen
			this.mapDef.layers = this.mapDef.layers.filter(ld => ld.name !== uniqueName);
			await this.confirmService.warning(`Fehler beim Importieren: ${e instanceof Error ? e.message : 'Unbekannter Fehler'}`);
		} finally {
			this.isAddingAttachment = false;
		}

		fi.value = '';
	}

	private getAttachmentType(filename: string): LayerType | undefined {
		const lower = filename.toLowerCase();
		if (lower.endsWith('.tif') || lower.endsWith('.tiff')) return 'geotiff';
		if (lower.endsWith('.gpkg')) return 'gpkg';
		if (lower.endsWith('.geojson') || lower.endsWith('.json')) return 'geojson';
		if (lower.endsWith('.shp') || lower.endsWith('.zip') || lower.endsWith('.7z')) return 'geojson'; // Shapefiles werden zu GeoJSON konvertiert
		return undefined;
	}

	onAddAttachment() {
		this.attachmentFileInput.nativeElement.click();
	}

	onSaveLayer(data: { ld: LayerDetails, index: number, layerRebuild: boolean }) {
		this.mapDef.layers[data.index] = data.ld;
	}

	onAddWmsLayer() {
		const ld = {
					type: "wms",
					url: "",
					name: "",
					isBase: false,
					minZoom: 0,
					maxZoom: 18,
					minSeedZoom: 4,
					maxSeedZoom: 15,
					tilesCount: 0,
					tilesMax: 0,
					transparent: true,
					bbox: new LatLngBounds([53.566414, 14.622803], [53.720279, 13.577728])
				} as SeedableLayerDetails;
		this.mapDef.layers.unshift(ld);
	}
	
	onRemoveLayer(i: number) {
		this.mapDef.layers.splice(i, 1);
	}
	
	onClose() {
		const mdRev = this.mapDef;
		mdRev.layers = this.mapDef.layers.reverse();
		this.activeModal.close(mdRev);
	}

	// === Zeichnungslayer-Verwaltung ===

	private async loadZeichnungslayer(): Promise<void> {
		const layers = await this.ps.getProjectValue('zeichnungslayer') as ZeichnungslayerDefinition[] | null;
		this.zeichnungslayer.set(layers ?? []);
	}

	async addZeichnungslayer(): Promise<void> {
		const newLayer: ZeichnungslayerDefinition = {
			id: crypto.randomUUID(),
			name: `Zeichnung ${this.zeichnungslayer().length + 1}`,
			visible: true,
			geojson: { type: 'FeatureCollection', features: [] },
			strokeColor: '#3388ff',
			strokeWidth: 3,
			fillColor: '#3388ff',
			fillOpacity: 0.2
		};

		const updated = [...this.zeichnungslayer(), newLayer];
		await this.ps.setProjectValue('zeichnungslayer', updated);
		this.zeichnungslayer.set(updated);
		this.ls.zeichnungslayerChanged.emit();
	}

	async deleteZeichnungslayer(layerId: string): Promise<void> {
		const confirmed = await this.confirmService.confirmDelete('Zeichnungslayer wirklich löschen?');
		if (!confirmed) return;

		const updated = this.zeichnungslayer().filter(l => l.id !== layerId);
		await this.ps.setProjectValue('zeichnungslayer', updated);
		this.zeichnungslayer.set(updated);
		this.ls.zeichnungslayerChanged.emit();
	}

	async renameZeichnungslayer(layerId: string, newName: string): Promise<void> {
		const updated = this.zeichnungslayer().map(l =>
			l.id === layerId ? { ...l, name: newName } : l
		);
		await this.ps.setProjectValue('zeichnungslayer', updated);
		this.zeichnungslayer.set(updated);
		this.ls.zeichnungslayerChanged.emit();
	}

	zoomToZeichnungslayer(layerId: string): void {
		this.ls.zeichnungslayerZoomRequested.emit(layerId);
	}

	// Getter für aktuelle Füllfarbe - gibt rgba zurück
	getFillColor(layer: ZeichnungslayerDefinition): string {
		const hex = layer.fillColor ?? '#3388ff';
		const opacity = layer.fillOpacity ?? 0.2;
		const r = parseInt(hex.slice(1, 3), 16);
		const g = parseInt(hex.slice(3, 5), 16);
		const b = parseInt(hex.slice(5, 7), 16);
		return `rgba(${r}, ${g}, ${b}, ${opacity})`;
	}

	// Getter für aktuelle Linienfarbe
	getStrokeColor(layer: ZeichnungslayerDefinition): string {
		return layer.strokeColor ?? '#3388ff';
	}

	// Öffnet das Style-Modal für einen Zeichnungslayer
	openStyleModal(layer: ZeichnungslayerDefinition): void {
		const modalRef = this.modalService.open(DrawStyleModalComponent, {
			centered: true,
			size: 'md'
		});
		modalRef.componentInstance.layerId = layer.id;
		modalRef.componentInstance.layerStyle = {
			strokeColor: layer.strokeColor ?? '#3388ff',
			strokeWidth: layer.strokeWidth ?? 3,
			fillColor: layer.fillColor ?? '#3388ff',
			fillOpacity: layer.fillOpacity ?? 0.2
		};

		modalRef.result.then(async (newStyle) => {
			if (newStyle) {
				const updated = this.zeichnungslayer().map(l =>
					l.id === layer.id ? { ...l, ...newStyle } : l
				);
				await this.ps.setProjectValue('zeichnungslayer', updated);
				this.zeichnungslayer.set(updated);
				this.ls.zeichnungslayerChanged.emit();
			}
		}).catch(() => {});
	}

	// === Punktgitter-Verwaltung ===

	private initGridLayer(): void {
		// Prüfen ob ein Grid-Layer in mapDef existiert
		const gridLayer = this.mapDef.layers.find(ld => ld.type === 'grid');
		if (gridLayer) {
			this.gridEnabled.set(gridLayer.display ?? false);
			this.gridResolution.set(gridLayer.resolution ?? 5);
			if (gridLayer.gridStyle) {
				this.gridStyle.set(gridLayer.gridStyle);
				this.gridRadius.set(gridLayer.gridStyle.radius);
			}
		}
	}

	toggleGrid(): void {
		const newState = !this.gridEnabled();
		this.gridEnabled.set(newState);
		this.updateGridInMapDef();
	}

	onGridEnabledChange(enabled: boolean): void {
		this.gridEnabled.set(enabled);
		this.updateGridInMapDef();
	}

	onGridStyleChange(style: GridStyle): void {
		this.gridStyle.set(style);
		this.gridRadius.set(style.radius);
		this.updateGridInMapDef();
	}

	onGridResolutionChange(value: string): void {
		const resolution = parseFloat(value);
		this.gridResolution.set(resolution);
		this.updateGridInMapDef();
	}

	onGridRadiusChange(value: string): void {
		const radius = parseInt(value);
		this.gridRadius.set(radius);
		const style = this.gridStyle();
		this.gridStyle.set({ ...style, radius });
		this.updateGridInMapDef();
	}

	getGridFillColor(): string {
		const style = this.gridStyle();
		const hex = style.fillColor;
		const opacity = style.fillOpacity;
		const r = parseInt(hex.slice(1, 3), 16);
		const g = parseInt(hex.slice(3, 5), 16);
		const b = parseInt(hex.slice(5, 7), 16);
		return `rgba(${r}, ${g}, ${b}, ${opacity})`;
	}

	getGridStrokeColor(): string {
		return this.gridStyle().color;
	}

	openGridStyleModal(): void {
		const modalRef = this.modalService.open(DrawStyleModalComponent, {
			centered: true,
			size: 'md'
		});
		const style = this.gridStyle();
		modalRef.componentInstance.layerId = 'grid';
		modalRef.componentInstance.layerStyle = {
			strokeColor: style.color,
			strokeWidth: 1,
			fillColor: style.fillColor,
			fillOpacity: style.fillOpacity
		};

		modalRef.result.then((newStyle) => {
			if (newStyle) {
				this.gridStyle.set({
					color: newStyle.strokeColor,
					fillColor: newStyle.fillColor,
					fillOpacity: newStyle.fillOpacity,
					radius: this.gridRadius()
				});
				this.updateGridInMapDef();
			}
		}).catch(() => {});
	}

	private updateGridInMapDef(): void {
		// Suche bestehenden Grid-Layer
		const gridLayerIndex = this.mapDef.layers.findIndex(ld => ld.type === 'grid');

		if (this.gridEnabled()) {
			if (gridLayerIndex === -1) {
				// Neuen Grid-Layer erstellen
				const gridLayer = this.ls.pointGrid();
				gridLayer.display = true;
				gridLayer.resolution = this.gridResolution();
				gridLayer.gridStyle = this.gridStyle();
				this.mapDef.layers.unshift(gridLayer);
			} else {
				// Einstellungen aktualisieren
				const gridLayer = this.mapDef.layers[gridLayerIndex];
				gridLayer.display = true;
				gridLayer.resolution = this.gridResolution();
				gridLayer.gridStyle = this.gridStyle();
			}
		} else {
			// Grid-Layer komplett entfernen
			if (gridLayerIndex !== -1) {
				this.mapDef.layers.splice(gridLayerIndex, 1);
			}
		}
	}
}
